import React, { useEffect, useRef } from "react";
import {
  ActivityIndicator,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { AlertCircle, CheckCircle } from "lucide-react";

export type CourseModernizeProgressStepStatus = "pending" | "in_progress" | "completed" | "failed";

export type CourseModernizeProgressStep = {
  key: string;
  title: string;
  agent: string;
  description: string;
  status: CourseModernizeProgressStepStatus;
};

type CourseModernizeProgressModalProps = {
  visible: boolean;
  onRequestClose: () => void;
  isModernizationTerminal: boolean;
  isEvaluationWorking: boolean;
  isModernizationCompleted: boolean;
  isModernizationFailed: boolean;
  statusMessage?: string | null;
  currentStepIndex: number;
  totalSteps: number;
  normalizedPercent: number;
  loading: boolean;
  steps: CourseModernizeProgressStep[];
  isCancellingEvaluation: boolean;
  onCancelEvaluation: () => void;
  onContinueToResults: () => void;
};

export function CourseModernizeProgressModal({
  visible,
  onRequestClose,
  isModernizationTerminal,
  isEvaluationWorking,
  isModernizationCompleted,
  isModernizationFailed,
  statusMessage,
  currentStepIndex,
  totalSteps,
  normalizedPercent,
  loading,
  steps,
  isCancellingEvaluation,
  onCancelEvaluation,
  onContinueToResults,
}: CourseModernizeProgressModalProps) {
  const AUTO_SCROLL_BOTTOM_THRESHOLD = 40;
  const progressScrollViewRef = useRef<ScrollView | null>(null);
  const previousVisibleStepCountRef = useRef(0);
  const isUserNearBottomRef = useRef(true);

  const failureMessage = statusMessage || "There was an error during modernization generation. Close and start again.";

  const modalTitle = isModernizationCompleted
    ? "Evaluation Complete"
    : isModernizationFailed
      ? "Evaluation Failed"
      : "Evaluating Course";

  const modalSubtitle = isModernizationCompleted
    ? "Full Course Review has finished. Click continue to view results."
    : "Scanning LMS course and evaluating against all framework criteria...";
  const visibleSteps = steps.filter((step) => step.status !== "pending");

  const handleProgressScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    isUserNearBottomRef.current = distanceFromBottom <= AUTO_SCROLL_BOTTOM_THRESHOLD;
  };

  useEffect(() => {
    const hasNewVisibleStep = visibleSteps.length > previousVisibleStepCountRef.current;

    if (visible && hasNewVisibleStep && isUserNearBottomRef.current) {
      progressScrollViewRef.current?.scrollToEnd({ animated: true });
    }

    previousVisibleStepCountRef.current = visibleSteps.length;
  }, [visible, visibleSteps.length]);

  return (
    <Modal transparent={true} visible={visible} animationType="fade" onRequestClose={onRequestClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <View>
              <CustomText
                text={modalTitle}
                size="s"
                textType="display"
                weight="bold"
                style={styles.modalTitle}
                useTranslationText={false}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomText text={modalSubtitle} size="s" style={styles.modalSubtitle} useTranslationText={false} />
            </View>
            {isModernizationTerminal || isEvaluationWorking ? (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel="Close modal"
                onPress={onRequestClose}
                style={styles.modalCloseButton}>
                <CustomText text="×" size="xxl" style={styles.modalCloseButtonText} useTranslationText={false} />
              </TouchableOpacity>
            ) : null}
          </View>

          <ScrollView
            ref={progressScrollViewRef}
            style={styles.modalBody}
            contentContainerStyle={styles.modalBodyContent}
            onScroll={handleProgressScroll}
            scrollEventThrottle={16}>
            <View style={styles.progressSummaryRow}>
              <CustomText
                text={`Step ${Math.min(Math.max(currentStepIndex, 1), totalSteps || 1)} of ${totalSteps || 0}`}
                size="s"
                weight="medium"
                style={styles.progressSummaryText}
                useTranslationText={false}
              />
              <CustomText
                text={`${normalizedPercent}%`}
                size="s"
                weight="medium"
                style={styles.progressSummaryText}
                useTranslationText={false}
              />
            </View>

            <View style={styles.progressBarTrack}>
              <View style={[styles.progressBarFill, { width: `${normalizedPercent}%` }]} />
            </View>

            {isModernizationFailed ? (
              <View style={styles.evaluationWarningCard}>
                <AlertCircle size={16} color="#B54708" />
                <CustomText
                  text={`Error: ${failureMessage}`}
                  size="xs"
                  style={styles.evaluationWarningText}
                  useTranslationText={false}
                />
              </View>
            ) : null}

            <View style={styles.stepListContainer}>
              {loading && visibleSteps.length === 0 ? (
                <View style={styles.progressLoadingState}>
                  <ActivityIndicator size="small" color="#3E68FE" />
                  <CustomText text="Loading live stage status..." size="s" useTranslationText={false} />
                </View>
              ) : visibleSteps.length === 0 ? (
                <View style={styles.progressLoadingState}>
                  <CustomText text="Waiting for stage metadata..." size="s" useTranslationText={false} />
                </View>
              ) : (
                visibleSteps.map((step) => {
                  const effectiveStepStatus = step.status;

                  return (
                    <View key={step.key} style={styles.stepRow}>
                      {effectiveStepStatus === "completed" ? (
                        <View style={styles.completedStepIndicator}>
                          <CheckCircle size={16} color="#66c61c" />
                        </View>
                      ) : (
                        <View
                          style={[
                            styles.stepIndicator,
                            effectiveStepStatus === "in_progress" && styles.stepIndicatorInProgress,
                            effectiveStepStatus === "failed" && styles.stepIndicatorFailed,
                          ]}
                        />
                      )}
                      <View style={styles.stepTextBlock}>
                        <View style={styles.stepTitleRow}>
                          <CustomText
                            text={`${step.title} · `}
                            size="s"
                            weight="medium"
                            style={[styles.stepTitle, effectiveStepStatus === "in_progress" && styles.stepTitleActive]}
                            useTranslationText={false}
                          />
                          <CustomText
                            text={step.agent}
                            size="s"
                            weight="medium"
                            style={styles.stepTitleAgent}
                            useTranslationText={false}
                          />
                          {effectiveStepStatus === "in_progress" ? (
                            <ActivityIndicator size={12} color="#3E68FE" style={styles.inlineStepSpinner} />
                          ) : null}
                        </View>
                        <CustomText
                          text={step.description}
                          size="xs"
                          style={styles.stepDescription}
                          useTranslationText={false}
                        />
                      </View>
                    </View>
                  );
                })
              )}
            </View>
          </ScrollView>

          <View style={styles.modalFooter}>
            {!isModernizationTerminal ? (
              <CustomButton
                text={isCancellingEvaluation ? "Cancelling..." : "Cancel Evaluation"}
                styleType="primaryTransparentStroke"
                size="s"
                disabled={isCancellingEvaluation}
                useTranslationText={false}
                onPress={onCancelEvaluation}
              />
            ) : null}
            {isModernizationCompleted ? (
              <CustomButton
                text="Continue to Results"
                styleType="primary"
                size="s"
                style={styles.footerButton}
                useTranslationText={false}
                onPress={onContinueToResults}
              />
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(16, 24, 40, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  modalContainer: {
    width: "100%",
    maxWidth: 600,
    maxHeight: "86%",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    overflow: "visible",
  },
  modalHeader: {
    backgroundColor: "#F9FAFB",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  modalTitle: {
    color: "#101828",
    fontSize: 20,
    lineHeight: 26,
  },
  modalSubtitle: {
    color: "#667085",
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  modalCloseButtonText: {
    color: "#98A2B3",
  },
  modalBody: {
    flex: 1,
  },
  modalBodyContent: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 14,
  },
  progressSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  progressSummaryText: {
    color: "#344054",
  },
  progressBarTrack: {
    height: 10,
    borderRadius: 999,
    backgroundColor: "#EAECF0",
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#3E68FE",
  },
  evaluationWarningCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    borderRadius: 8,
    backgroundColor: "#FFFAEB",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  evaluationWarningText: {
    color: "#475467",
    flex: 1,
  },
  stepListContainer: {
    borderRadius: 12,
    paddingVertical: 6,
    overflow: "hidden",
  },
  progressLoadingState: {
    paddingVertical: 18,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  stepRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  stepIndicator: {
    width: 16,
    height: 16,
    borderRadius: 999,
    backgroundColor: "#D0D5DD",
    marginTop: 5,
  },
  completedStepIndicator: {
    width: 16,
    height: 16,
    marginTop: 2,
  },
  stepIndicatorInProgress: {
    backgroundColor: "#FFFFFF",
    borderWidth: 3,
    borderColor: "#3E68FE",
  },
  stepIndicatorFailed: {
    backgroundColor: "#F04438",
  },
  stepTextBlock: {
    flex: 1,
    gap: 3,
  },
  stepTitleRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  stepTitle: {
    color: "#101828",
  },
  stepTitleAgent: {
    color: "#667085",
  },
  stepTitleActive: {
    color: "#175CD3",
  },
  stepDescription: {
    color: "#667085",
  },
  inlineStepSpinner: {
    marginLeft: 8,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 24,
  },
  footerButton: {
    width: "100%",
    justifyContent: "center",
    borderRadius: 10,
  },
});
